import { CircularProgress, Grid, TableCell, TableRow, Tooltip, Typography, Box } from '@mui/material';
import ComputerIcon from '@mui/icons-material/Computer';
import { Documents } from '@assets/index';
import { useFetch } from '@hooks/useFetch';
import { SystemUrl, fetchSystemData } from '@api/system';
import { useSelector } from 'react-redux';
import { GenericTable } from '@components/GenericTable';
import { useNavigate } from 'react-router-dom';
import { useOrgDetailLink } from '../../useOrgDetailLink';
import { AppRoutes } from '@consts/routes';
import { SystemModelMap } from '@consts/systems';
import { InfoCard } from '@components/design-system';

import type { System } from '@api/system';
import type { RootState } from '@features/redux/store';
import { isAdmin } from '@utils/permissions';
import { useStyles } from './styles/LocationSystems.styles';

interface LocationSystemsProps {
    locationId: number | undefined;
}

export const LocationSystems = ({ locationId }: LocationSystemsProps) => {
    const { IdToken } = useSelector((state: RootState) => state.auth);

    const navigate = useNavigate();
    const detailLink = useOrgDetailLink();
    const classes = useStyles();

    // todo: Use RTKQuery
    const { data, loading } = useFetch({
        spinner: false,
        defaultValue: [],
        fetcher: async () => {
            return locationId
                ? fetchSystemData<System[]>(IdToken!, SystemUrl.StationsByLocation, {
                      locationId,
                      type: 'assigned',
                  })
                : [];
        },
    });

    const admin = isAdmin(IdToken!);

    const headers = [
        { label: 'Model', width: 100 },
        { label: 'Serial Number', width: 100 },
        { label: 'Status', width: 100 },
    ];

    const adminHeaders = [{ label: 'Station ID', width: 100 }];

    const allHeaders = admin ? [...headers, ...adminHeaders] : headers;

    return (
        <InfoCard title="Systems at Location" icon={<ComputerIcon />}>
            <Box sx={{ minHeight: 150 }}>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 150 }}>
                        <CircularProgress size='2.5rem' />
                    </Box>
                ) : data.length === 0 ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: 150, gap: 1 }}>
                        <Documents />
                        <Typography fontWeight='500' color='text.secondary'>
                            There are no RapidCut systems assigned to this location.
                        </Typography>
                    </Box>
                ) : (
                    <GenericTable maxHeight={300} headers={allHeaders}>
                        {data.map((system) => (
                            <Tooltip key={system.StationID} title={'Click to manage'} placement='top'>
                                <TableRow
                                    className={classes.row}
                                    onClick={() =>
                                        navigate(detailLink('system', system.StationID ?? ''))
                                    }>
                                    <TableCell style={{ padding: '10px' }}>
                                        {(system.ModelName !== 'Unknown' && system.ModelName) ||
                                            SystemModelMap[system.Model?.toString() as string] ||
                                            'Unknown Model'}
                                    </TableCell>
                                    <TableCell style={{ padding: '10px' }}>{system.SerialNum || '-'}</TableCell>
                                    <TableCell style={{ padding: '10px' }}>{system.Status || '-'}</TableCell>
                                    {admin && (
                                        <TableCell style={{ padding: '10px' }}>{system.StationID}</TableCell>
                                    )}
                                </TableRow>
                            </Tooltip>
                        ))}
                    </GenericTable>
                )}
            </Box>
        </InfoCard>
    );
};
